import {cn} from '@heroui/react';
import {FieldRound, FieldTable, positionLabel, scoringLabel, shownScore, toPar} from './api';

/**
 * The read-only field tables: one round's leaderboard, and the tables across rounds (the rounds
 * added up, or the season series' order of merit). Shared by the organiser's overview, the public
 * event page and the Live View / TV Mode. `tone="dark"` for the dark public pages.
 */
type Tone = 'light' | 'dark';

const shell = (tone: Tone) => cn('overflow-x-auto rounded-2xl border', tone === 'dark' ? 'border-white/10 bg-white/[0.04] text-white' : 'border-default-200 bg-content1 text-foreground');
const head = (tone: Tone) => cn('text-left text-tiny font-semibold uppercase tracking-wide', tone === 'dark' ? 'text-white/60' : 'text-default-500');
const rowLine = (tone: Tone) => (tone === 'dark' ? 'border-t border-white/10' : 'border-t border-default-100');

export function RoundTable({round, tone = 'light', divisionName, big = false}: {round: FieldRound; tone?: Tone; divisionName?: string | null; big?: boolean}) {
    const pinfall = round.scoring === 'pinfall';
    const scoreHead = round.scoring === 'stableford' ? 'Points' : pinfall ? 'Pins' : round.handicap_enabled ? 'Net' : 'Strokes';
    const showPar = round.scoring === 'stroke' && !!round.course;
    return (
        <div className={shell(tone)} data-testid={`field-round-${round.uid}`}>
            <div className={cn('flex flex-wrap items-baseline justify-between gap-2 px-4 pt-3', tone === 'dark' ? 'text-white' : '')}>
                <h4 className={cn('font-bold', big ? 'text-2xl' : 'text-base')}>{round.name}{divisionName ? ` · ${divisionName}` : ''}</h4>
                <span className={cn('text-tiny', tone === 'dark' ? 'text-white/60' : 'text-default-500')}>
                    {scoringLabel(round.scoring)} · {round.holes_or_games} {pinfall ? 'games' : 'holes'}{round.handicap_enabled ? ' · handicap' : ''}
                    {round.status === 'completed' ? ' · final' : round.status === 'in_progress' ? ' · in play' : ''}
                </span>
            </div>
            <table className={cn('mt-2 w-full', big ? 'text-lg' : 'text-sm')}>
                <thead className={head(tone)}>
                    <tr>
                        <th className="w-12 px-4 py-1.5">Pos</th>
                        <th className="py-1.5">Player</th>
                        {round.handicap_enabled && <th className="w-14 py-1.5 text-right">Hcp</th>}
                        {showPar && <th className="w-14 py-1.5 text-right">To par</th>}
                        {round.handicap_enabled && round.scoring !== 'stableford' && <th className="w-16 py-1.5 text-right">{pinfall ? 'Scratch' : 'Gross'}</th>}
                        <th className="w-16 py-1.5 text-right">{scoreHead}</th>
                        <th className="w-16 px-4 py-1.5 text-right">Thru</th>
                    </tr>
                </thead>
                <tbody>
                    {round.entries.map((e) => {
                        const out = e.status !== 'finished';
                        return (
                            <tr key={e.uid} className={cn(rowLine(tone), out && 'opacity-60')}>
                                <td className="px-4 py-1.5 font-bold tabular-nums">{out ? e.status : positionLabel(e.position, e.tied)}</td>
                                <td className="py-1.5">{e.display_name}</td>
                                {round.handicap_enabled && <td className="py-1.5 text-right tabular-nums">{e.handicap ?? ''}</td>}
                                {showPar && <td className="py-1.5 text-right tabular-nums">{toPar(round, e) ?? ''}</td>}
                                {round.handicap_enabled && round.scoring !== 'stableford' && <td className="py-1.5 text-right tabular-nums">{out ? '' : e.gross ?? ''}</td>}
                                <td className="py-1.5 text-right font-bold tabular-nums">{out ? '' : shownScore(round, e) ?? ''}</td>
                                <td className="px-4 py-1.5 text-right tabular-nums">{e.thru === null ? '' : e.thru === round.holes_or_games ? 'F' : e.thru}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {round.entries.length === 0 && <p className="px-4 pb-3 text-sm opacity-70">No entrants yet.</p>}
        </div>
    );
}

/**
 * Across rounds: `leaderboard` adds the rounds up (players missing one list after, unplaced);
 * `series` shows each round's order-of-merit points and the counted total.
 */
export function TotalsTable({table, rounds, kind, tone = 'light', divisionName, big = false}: {
    table: FieldTable; rounds: FieldRound[]; kind: 'leaderboard' | 'series'; tone?: Tone; divisionName?: string | null; big?: boolean;
}) {
    const cols = table.rounds.map((uid) => rounds.find((r) => r.uid === uid)).filter(Boolean) as FieldRound[];
    const title = kind === 'series' ? 'Order of merit' : 'Leaderboard';
    return (
        <div className={shell(tone)} data-testid={`field-${kind}`}>
            <div className={cn('flex flex-wrap items-baseline justify-between gap-2 px-4 pt-3', tone === 'dark' ? 'text-white' : '')}>
                <h4 className={cn('font-bold', big ? 'text-2xl' : 'text-base')}>{title}{divisionName ? ` · ${divisionName}` : ''}</h4>
                <span className={cn('text-tiny', tone === 'dark' ? 'text-white/60' : 'text-default-500')}>
                    {kind === 'series'
                        ? `Points by finishing place in each completed round${table.best_n ? ` · best ${table.best_n} count` : ''}`
                        : `${cols.length} round${cols.length === 1 ? '' : 's'} added up${table.lower_wins ? ' · lowest wins' : ' · highest wins'}`}
                </span>
            </div>
            <table className={cn('mt-2 w-full', big ? 'text-lg' : 'text-sm')}>
                <thead className={head(tone)}>
                    <tr>
                        <th className="w-12 px-4 py-1.5">Pos</th>
                        <th className="py-1.5">Player</th>
                        {cols.map((r) => <th key={r.uid} className="w-16 py-1.5 text-right">R{r.round_number}</th>)}
                        <th className="w-20 px-4 py-1.5 text-right">{kind === 'series' ? 'Points' : 'Total'}</th>
                    </tr>
                </thead>
                <tbody>
                    {table.rows.map((row) => (
                        <tr key={row.key} className={cn(rowLine(tone), row.position === null && 'opacity-60')}>
                            <td className="px-4 py-1.5 font-bold tabular-nums">{row.position === null ? (row.status ?? '') : positionLabel(row.position, row.tied)}</td>
                            <td className="py-1.5">{row.name}</td>
                            {cols.map((r) => <td key={r.uid} className="py-1.5 text-right tabular-nums">{row.rounds[r.uid] ?? ''}</td>)}
                            <td className="px-4 py-1.5 text-right font-bold tabular-nums">{row.total ?? ''}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {table.rows.length === 0 && <p className="px-4 pb-3 text-sm opacity-70">Nothing to show yet.</p>}
        </div>
    );
}
